import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Zap, Pencil, Check, X, Loader2 } from "lucide-react";

const ROWS = [
  ["flexible", "Flexible"],
  ["30", "30 day"],
  ["90", "90 day"],
  ["180", "180 day"],
  ["360", "360 day"],
  ["540", "540 day"],
];

const when = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days}d ago`;
};

/**
 * LiveStakingRates — the CaryPact plan rates the whole app calculates with
 * (calculator, capital-recovery scenarios, each position's rate/day, Stuart's
 * answers). A wallet sync refreshes them from CaryPact's own settings table;
 * an admin can also correct a number by hand.
 */
export default function LiveStakingRates() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/staking-rates").then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const rates = data.rates || {};
  const canEdit = user?.role === "admin" && !user?.is_demo;
  const synced = data.source === "carypact";

  const save = async () => {
    const body = {};
    for (const [k] of ROWS) {
      const v = String(edit[k] ?? "").trim();
      if (v === "") continue;
      const n = Number(v);
      if (Number.isNaN(n) || n < 0 || n > 5) { toast.error(`${k}: enter a %/day between 0 and 5`); return; }
      body[k] = n;
    }
    setSaving(true);
    try {
      const { data: d } = await api.put("/staking-rates", body);
      setData(d);
      setEdit(null);
      toast.success("Staking rates updated — the calculator uses them right away");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the rates");
    } finally { setSaving(false); }
  };

  return (
    <div className="glass-strong p-4" data-testid="live-staking-rates">
      <div className="flex items-center gap-2 mb-2">
        <Zap size={14} className="text-[#00863A]" />
        <h3 className="font-display text-sm font-semibold tracking-tight flex-1">Live staking rates</h3>
        {canEdit && !edit && (
          <button type="button" onClick={() => setEdit({ ...rates })} title="Correct a rate by hand"
            className="text-ink-3 hover:text-[#7C3AED] transition-colors" data-testid="live-rates-edit-btn">
            <Pencil size={12} />
          </button>
        )}
        {edit && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={save} disabled={saving} className="text-[#00863A] disabled:opacity-40" data-testid="live-rates-save-btn" title="Save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
            </button>
            <button type="button" onClick={() => setEdit(null)} className="text-ink-3 hover:text-rose-600" data-testid="live-rates-cancel-btn" title="Cancel">
              <X size={13} />
            </button>
          </div>
        )}
      </div>
      <p className="text-[10px] text-ink-3 leading-snug mb-2" data-testid="live-rates-source">
        {synced
          ? `Pulled from CaryPact's own plan table${when(data.updated_at) ? ` · ${when(data.updated_at)}` : ""} — refreshes on every wallet sync.`
          : data.source === "manual"
            ? `Set by hand${when(data.updated_at) ? ` · ${when(data.updated_at)}` : ""}${data.updated_by ? ` by ${data.updated_by}` : ""} — the next one-tap wallet sync will refresh these from CaryPact.`
            : "Starting values — run a one-tap wallet sync (or edit them here) so every calculation matches the CaryPact dApp."}
      </p>

      <ul className="space-y-1 text-xs font-mono tabular-nums" data-testid="live-rates-list">
        {ROWS.map(([k, label]) => (
          <li key={k} className="flex items-center justify-between gap-2" data-testid={`live-rates-row-${k}`}>
            <span className="text-ink-2">{label}</span>
            {edit ? (
              <span className="flex items-center gap-1">
                <input
                  value={edit[k] ?? ""}
                  onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
                  inputMode="decimal"
                  className="w-16 text-right px-1.5 py-0.5 rounded border bg-white text-xs"
                  style={{ borderColor: "rgba(15,18,38,0.15)" }}
                  data-testid={`live-rates-input-${k}`}
                />
                <span className="text-ink-3">%/day</span>
              </span>
            ) : (
              <span className="font-semibold text-ink">{rates[k] != null ? `${rates[k]}%/day` : <span className="text-ink-3">—</span>}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
